/**
 * Authentication Context for ParlSearch
 * Manages user authentication state, tokens, and auth operations
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AuthContext = createContext(null);

// Token storage keys
const ACCESS_TOKEN_KEY = 'parlsearch_access_token';
const REFRESH_TOKEN_KEY = 'parlsearch_refresh_token';
const USER_KEY = 'parlsearch_user';

// Token refresh interval constants
// eslint-disable-next-line no-unused-vars
const REFRESH_THRESHOLD = 60 * 1000; // 1 minute in ms (for future use)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get stored tokens
  const getAccessToken = useCallback(() => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }, []);

  const getRefreshToken = useCallback(() => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }, []);

  // Store tokens
  const setTokens = useCallback((accessToken, refreshToken) => {
    if (accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }, []);

  // Clear all auth data
  const clearAuth = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  // Refresh access token
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearAuth();
      return null;
    }

    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        refresh_token: refreshToken
      });

      const { access_token, refresh_token: newRefreshToken } = response.data;
      setTokens(access_token, newRefreshToken);
      return access_token;
    } catch (err) {
      console.error('Token refresh failed:', err);
      clearAuth();
      return null;
    }
  }, [getRefreshToken, setTokens, clearAuth]);

  // Fetch current user profile
  const fetchUser = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      localStorage.setItem(USER_KEY, JSON.stringify(response.data));
    } catch (err) {
      if (err.response?.status === 401) {
        // Try to refresh token
        const newToken = await refreshAccessToken();
        if (newToken) {
          try {
            const response = await axios.get(`${API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${newToken}` }
            });
            setUser(response.data);
            localStorage.setItem(USER_KEY, JSON.stringify(response.data));
          } catch (retryErr) {
            clearAuth();
          }
        }
      } else {
        clearAuth();
      }
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, refreshAccessToken, clearAuth]);

  // Initialize auth state on mount
  useEffect(() => {
    // Try to load user from localStorage first for faster initial render
    const storedUser = localStorage.getItem(USER_KEY);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        // Invalid stored user
      }
    }

    // Then verify with the server
    fetchUser();
  }, [fetchUser]);

  // Set up token refresh interval
  useEffect(() => {
    if (!user) return;

    // Refresh token periodically (every 14 minutes for 15 min token)
    const interval = setInterval(() => {
      refreshAccessToken();
    }, 14 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, refreshAccessToken]);

  // Register new user
  const register = async (email, password, displayName) => {
    setError(null);
    try {
      await axios.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        display_name: displayName
      });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Login user
  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      const { access_token, refresh_token } = response.data;
      setTokens(access_token, refresh_token);

      // Fetch user profile
      const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });

      setUser(userResponse.data);
      localStorage.setItem(USER_KEY, JSON.stringify(userResponse.data));

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Logout user
  const logout = async () => {
    const refreshToken = getRefreshToken();

    // Revoke token on server (best effort)
    if (refreshToken) {
      try {
        await axios.post(`${API_URL}/api/auth/logout`, {
          refresh_token: refreshToken
        });
      } catch (err) {
        // Ignore errors, still clear local state
      }
    }

    clearAuth();
  };

  // Logout from all devices
  const logoutAll = async () => {
    const token = getAccessToken();

    if (token) {
      try {
        await axios.post(`${API_URL}/api/auth/logout-all`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        // Ignore errors
      }
    }

    clearAuth();
  };

  // Get current access token (for API calls)
  const getToken = useCallback(() => {
    return getAccessToken();
  }, [getAccessToken]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    logoutAll,
    getToken,
    refreshAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
